import { useEffect } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import {
  CONTRACTOR_APP_ORIGIN,
  IS_CONTRACTOR_APP,
  IS_STAFF_CONSOLE,
  STAFF_ROOT_REDIRECT,
} from "@/appTarget";
import { Layout } from "@/components/Layout";
import { RequireAuth } from "@/components/RequireAuth";
import { AuditLogPage } from "@/routes/AuditLogPage";
import { BillingEventsPage } from "@/routes/BillingEventsPage";
import { ClaimSignupPage } from "@/routes/ClaimSignupPage";
import { ClientDetailPage } from "@/routes/ClientDetailPage";
import { ClientsPage } from "@/routes/ClientsPage";
import { ConsumeEmailChangePage } from "@/routes/ConsumeEmailChangePage";
import { ContractorSettingsPage } from "@/routes/ContractorSettingsPage";
import { DashboardPage } from "@/routes/DashboardPage";
import { EmailEventsPage } from "@/routes/EmailEventsPage";
import { FailedWalkthroughsPage } from "@/routes/FailedWalkthroughsPage";
import { InboxPage } from "@/routes/InboxPage";
import { JobDetailPage } from "@/routes/JobDetailPage";
import { JobTemplatesPage } from "@/routes/JobTemplatesPage";
import { JobsPage } from "@/routes/JobsPage";
import { LoginPage } from "@/routes/LoginPage";
import { MagicLinkConsumePage } from "@/routes/MagicLinkConsumePage";
import { NotFoundPage } from "@/routes/NotFoundPage";
import { OrganizationsPage } from "@/routes/OrganizationsPage";
import { PlanBillingPage } from "@/routes/PlanBillingPage";
import { PricingCalculatorPage } from "@/routes/PricingCalculatorPage";
import { PricingRulesPage } from "@/routes/PricingRulesPage";
import { ProfilePage } from "@/routes/ProfilePage";
import { PublicQuoteView } from "@/routes/PublicQuoteView";
import { QuoteCustomerPreviewPage } from "@/routes/QuoteCustomerPreviewPage";
import { QuoteDetailPage } from "@/routes/QuoteDetailPage";
import { QuotesListPage } from "@/routes/QuotesListPage";
import { SettingsPage } from "@/routes/SettingsPage";
import { SignupPage } from "@/routes/SignupPage";
import { SubcontractorsPage } from "@/routes/SubcontractorsPage";
import { TelemetryPage } from "@/routes/TelemetryPage";
import { UsagePage } from "@/routes/UsagePage";
import { UsersPage } from "@/routes/UsersPage";
import { WalkthroughDetailPage } from "@/routes/WalkthroughDetailPage";
import { WebhookEndpointsPage } from "@/routes/WebhookEndpointsPage";
import { PrivacyPage } from "@/routes/legal/PrivacyPage";
import { TermsPage } from "@/routes/legal/TermsPage";

/** Bounce to a different host (the contractor SPA) for a path
 *  that lives on a different build. Used by the staff console
 *  for routes like /signup and /q/:token that are intentionally
 *  contractor-only.
 *
 *  Renders a quiet placeholder during the bounce — about 100ms
 *  in practice — so the user doesn't see a flash of staff chrome
 *  before the navigation kicks in.
 */
function HostBounce({
  path,
  target,
  preservePath,
}: {
  /** Explicit destination path on the target host. Ignored when
   *  ``preservePath`` is true (current pathname is reused). */
  path?: string;
  target: "contractor";
  /** When true, append the current ``window.location.pathname``
   *  to the target origin instead of using ``path``. Right answer
   *  for parameterised routes like ``/q/:token`` where the path
   *  isn't known statically. */
  preservePath?: boolean;
}) {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const origin = target === "contractor" ? CONTRACTOR_APP_ORIGIN : CONTRACTOR_APP_ORIGIN;
    const dest = preservePath
      ? `${origin}${window.location.pathname}${window.location.search}`
      : `${origin}${path ?? "/"}`;
    window.location.assign(dest);
  }, [path, target, preservePath]);
  return (
    <div className="flex min-h-screen items-center justify-center bg-paper text-graphite-soft">
      <span className="font-mono text-sm">Redirecting…</span>
    </div>
  );
}

/** Top-level routing.
 *
 * The same React codebase ships under two build targets — see
 * ``appTarget.ts``. ``app.pencilout.app`` (contractor app) renders
 * the workspace + public quote view. ``admin.pencilout.app`` (staff
 * console) renders only staff-only routes (settings, organizations,
 * etc.). Auth + legal pages render on both targets so a magic-link
 * email's ``/auth/consume?token=...`` URL works regardless of which
 * subdomain it points at.
 */
export function App() {
  return (
    <Routes>
      {/* Shared on both targets — auth + legal. */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/consume" element={<MagicLinkConsumePage />} />
      <Route path="/auth/claim-signup" element={<ClaimSignupPage />} />
      <Route path="/auth/consume-email-change" element={<ConsumeEmailChangePage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />

      {/* Contractor app only. */}
      {IS_CONTRACTOR_APP && (
        <>
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/q/:token" element={<PublicQuoteView />} />
          <Route element={<RequireAuth />}>
            {/* Customer-view preview lives OUTSIDE <Layout /> so the
                contractor sees exactly what the homeowner sees on
                ``/q/{token}`` — no contractor sidebar, no app
                chrome. Authed (so only an org user can preview),
                side-effect-free (no view-counter bump). */}
            <Route
              path="/quotes/:quoteId/preview"
              element={<QuoteCustomerPreviewPage />}
            />
            <Route element={<Layout />}>
              <Route path="/" element={<DashboardPage />} />
              <Route path="/jobs" element={<JobsPage />} />
              <Route path="/quotes" element={<QuotesListPage />} />
              <Route path="/inbox" element={<InboxPage />} />
              <Route path="/clients" element={<ClientsPage />} />
              <Route path="/clients/:clientId" element={<ClientDetailPage />} />
              <Route path="/subcontractors" element={<SubcontractorsPage />} />
              <Route path="/jobs/:jobId" element={<JobDetailPage />} />
              <Route path="/walkthroughs/:walkthroughId" element={<WalkthroughDetailPage />} />
              <Route path="/quotes/:quoteId" element={<QuoteDetailPage />} />
              <Route path="/pricing-rules" element={<PricingRulesPage />} />
              <Route path="/job-templates" element={<JobTemplatesPage />} />
              <Route path="/webhooks" element={<WebhookEndpointsPage />} />
              <Route path="/billing" element={<PlanBillingPage />} />
              <Route path="/settings" element={<ContractorSettingsPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              {/* /usage (token / vendor-cost detail) is staff-only — see staff
                  console build below. Contractors get their billing-relevant
                  metric (quotes-this-cycle) on /billing. */}
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Route>
        </>
      )}

      {/* Staff console only. */}
      {IS_STAFF_CONSOLE && (
        <>
          {/* Routes that LIVE on the contractor app — bounce
              the user there rather than 404'ing on the staff
              host. Hits via stale ad/email links, marketing
              bookmarks, or a typed-URL accident all land
              somewhere useful. The shared cookie domain means
              session continuity is preserved on the bounce. */}
          <Route path="/signup" element={<HostBounce path="/signup" target="contractor" />} />
          <Route path="/q/:token" element={<HostBounce target="contractor" preservePath />} />
          <Route element={<RequireAuth />}>
            <Route element={<Layout />}>
              {/* Staff don't have a dashboard yet; bounce / to /organizations. */}
              <Route path="/" element={<Navigate to={STAFF_ROOT_REDIRECT} replace />} />
              <Route path="/organizations" element={<OrganizationsPage />} />
              <Route path="/users" element={<UsersPage />} />
              {/* Staff version of /pricing-rules: same component as
                  the contractor app, but the page-level role checks
                  expose the Add-missing + Reset buttons that gate
                  on isPencilOutAdmin. Staff picks an org via the
                  filter dropdown to target. */}
              <Route path="/pricing-rules" element={<PricingRulesPage />} />
              <Route path="/usage" element={<UsagePage />} />
              <Route path="/pricing-calculator" element={<PricingCalculatorPage />} />
              <Route path="/audit" element={<AuditLogPage />} />
              <Route path="/billing-events" element={<BillingEventsPage />} />
              <Route path="/email-events" element={<EmailEventsPage />} />
              <Route path="/failed-walkthroughs" element={<FailedWalkthroughsPage />} />
              <Route path="/telemetry" element={<TelemetryPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Route>
        </>
      )}
    </Routes>
  );
}
